import { IExpectedHoursItem } from "../services/types/expectedHours";
import appErrorToast from "../components/common/AppErrorToast";
import dataTimeService from "./dataTime.service";
import axiosInstance from "../utils/axios";
import { WorkTimeSettlement } from "./types/worktimeSettlement";
import apiService from "./api.service";

const pathPrefix = `${process.env.REACT_APP_SERVER_URL}/api`;

export interface IWorkItemsFilters {
  employeeId?: string;
  contractType?: "B2B" | "EmploymentContract" | "ContractOfMandate";
  onlyWithoutContractType?: boolean;
  settlementStatus?: "Pending" | "Rejected" | "Accepted";
  onlyWithoutSettlementDecision?: boolean;
}

async function getWorkMinutesByUserForMonth(
  month: number,
  year: number,
  filters?: IWorkItemsFilters
): Promise<WorkTimeSettlement[]> {
  const params: { key: string; value: string }[] = [];

  if (filters?.employeeId) params.push({ key: "employeeId", value: filters.employeeId });
  if (filters?.contractType) params.push({ key: "contractType", value: filters.contractType });
  if (filters?.onlyWithoutContractType) params.push({ key: "onlyWithoutContractType", value: "true" });
  if (filters?.settlementStatus) params.push({ key: "settlementStatus", value: filters.settlementStatus });
  if (filters?.onlyWithoutSettlementDecision) params.push({ key: "onlyWithoutSettlementDecision", value: "true" });

  let url = `${pathPrefix}/workItems?year=${year}&month=${month}`;
  if (params.length > 0) url = apiService.setPathParams(url, params);

  return axiosInstance.get<WorkTimeSettlement[]>(url)
    .then((response) => {
      return Promise.resolve(response.data);
    });
}

export interface ISettlementActionPayload {
  userId: string;
  year: number;
  month: number;
}

export interface IRejectSettlementActionPayload extends ISettlementActionPayload {
  rejectionReason: string;
}

export async function getExpectedHoursForMonth(
  year: number,
  month: number,
  filters?: IWorkItemsFilters
): Promise<IExpectedHoursItem[]> {
  try {
    const settlements = await getWorkMinutesByUserForMonth(month, year, filters);

    return settlements.map((x) => {
      const absentify = x.absentifyInMinutes ?? 0;
      const collected = x.collectedTimeInMinutes ?? 0;
      const workingHours = x.workingHoursInMinutes ?? 0;
      const toCollect = x.timeToCollectInMinutes ?? 0;

      return {
        id: `${x.userId}-${year}-${month}`,
        userId: x.userId,
        year,
        month,
        userName: `${x.lastName} ${x.firstName}`,
        contractType: x.contractType,
        youtrack: dataTimeService.convertMinutesToHours(x.workTimeInMinutes),
        absentify: dataTimeService.convertMinutesToHours(absentify),
        toCollect: dataTimeService.convertMinutesToHours(toCollect),
        collected: dataTimeService.convertMinutesToHours(collected),
        timePaidout100: dataTimeService.convertMinutesToHours(x.timeToPay100InMinutes ?? 0),
        timePaidout150: dataTimeService.convertMinutesToHours(x.timeToPay150InMinutes ?? 0),
        timePaidout200: dataTimeService.convertMinutesToHours(x.timeToPay200InMinutes ?? 0),
        workingHours: dataTimeService.convertMinutesToHours(workingHours),
        total: x.totalInHours,
        target: x.targetInHours,
        settlementStatus: x.settlementStatus,
        rejectionReason: x.rejectionReason ?? "",
        decisionAt: x.decisionAt,
        decisionByUserId: x.decisionByUserId,
        isValuesConsistent: x.isValuesConsistent,
        baselineDataChangedAfterDecision: x.baselineDataChangedAfterDecision,
        baselineDataChangedAtUtc: x.baselineDataChangedAtUtc ?? null,
        baselineDataChangedByUserId: x.baselineDataChangedByUserId ?? null,
        baselineDataChangeReason: x.baselineDataChangeReason ?? null,
      }
    })
  } catch (error) {
    appErrorToast(`Błąd w pobieraniu danych rozliczenia: ${error}`);
    return [];
  }
}

export async function acceptSettlement(payload: ISettlementActionPayload): Promise<void> {
  try {
    const url = `${pathPrefix}/workItems/${payload.userId}/settlement/accept?year=${payload.year}&month=${payload.month}`;
    await axiosInstance.put(url);
  } catch (error) {
    appErrorToast(`Błąd podczas akceptacji rozliczenia: ${error}`);
    throw error;
  }
}

export async function rejectSettlement(payload: IRejectSettlementActionPayload): Promise<void> {
  try {
    const url = `${pathPrefix}/workItems/${payload.userId}/settlement/reject?year=${payload.year}&month=${payload.month}`;
    await axiosInstance.put(url, { rejectionReason: payload.rejectionReason });
  } catch (error) {
    appErrorToast(`Błąd podczas odrzucania rozliczenia: ${error}`);
    throw error;
  }
}